// FilesList.js
import React, { useState } from 'react';
import { 
  FileText,
  Image,
  Film,
  Music,
  Archive,
  Code,
  Database,
  FileSpreadsheet,
  Presentation,
  File,
  Download,
  Trash2,
  Share2,
  Eye,
  MoreVertical,
  Lock,
  Unlock,
  Clock,
  User,
  HardDrive,
  Filter,
  Search,
  Upload,
  FolderOpen,
  Star,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import BackendTemplateConfig from '@/configs/BackendTemplateConfig';
import { useDrawer } from './manuela';
import SecureFileUpload from '@/components/custom/Upload';

const FilesList = () => {
  const config = BackendTemplateConfig;
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // all, recent, starred, shared
  const { openDrawer } = useDrawer();

  // File type configurations with icons and colors
  const fileTypes = {
    pdf: { 
      icon: FileText, 
      color: config.colors.danger['600'], 
      bgColor: config.colors.danger['50'],
      label: 'PDF Document' 
    },
    doc: { 
      icon: FileText, 
      color: config.colors.primary['600'], 
      bgColor: config.colors.primary['50'],
      label: 'Word Document' 
    },
    docx: { 
      icon: FileText, 
      color: config.colors.primary['600'], 
      bgColor: config.colors.primary['50'],
      label: 'Word Document' 
    },
    xls: { 
      icon: FileSpreadsheet, 
      color: config.colors.success['600'], 
      bgColor: config.colors.success['50'],
      label: 'Excel Spreadsheet' 
    },
    xlsx: { 
      icon: FileSpreadsheet, 
      color: config.colors.success['600'], 
      bgColor: config.colors.success['50'],
      label: 'Excel Spreadsheet' 
    },
    ppt: { 
      icon: Presentation, 
      color: 'text-orange-600', 
      bgColor: 'bg-orange-50',
      label: 'PowerPoint' 
    },
    pptx: { 
      icon: Presentation, 
      color: 'text-orange-600', 
      bgColor: 'bg-orange-50',
      label: 'PowerPoint' 
    },
    jpg: { 
      icon: Image, 
      color: 'text-purple-600', 
      bgColor: 'bg-purple-50',
      label: 'Image' 
    },
    jpeg: { 
      icon: Image, 
      color: 'text-purple-600', 
      bgColor: 'bg-purple-50',
      label: 'Image' 
    },
    png: { 
      icon: Image, 
      color: 'text-purple-600', 
      bgColor: 'bg-purple-50',
      label: 'Image' 
    },
    gif: { 
      icon: Image, 
      color: 'text-purple-600', 
      bgColor: 'bg-purple-50',
      label: 'Image' 
    },
    mp4: { 
      icon: Film, 
      color: 'text-pink-600', 
      bgColor: 'bg-pink-50',
      label: 'Video' 
    },
    avi: { 
      icon: Film, 
      color: 'text-pink-600', 
      bgColor: 'bg-pink-50',
      label: 'Video' 
    },
    mp3: { 
      icon: Music, 
      color: 'text-indigo-600', 
      bgColor: 'bg-indigo-50',
      label: 'Audio' 
    },
    wav: { 
      icon: Music, 
      color: 'text-indigo-600', 
      bgColor: 'bg-indigo-50',
      label: 'Audio' 
    },
    zip: { 
      icon: Archive, 
      color: config.colors.warning['600'], 
      bgColor: config.colors.warning['50'],
      label: 'Archive' 
    },
    rar: { 
      icon: Archive, 
      color: config.colors.warning['600'], 
      bgColor: config.colors.warning['50'],
      label: 'Archive' 
    },
    js: { 
      icon: Code, 
      color: 'text-yellow-600', 
      bgColor: 'bg-yellow-50',
      label: 'JavaScript' 
    },
    jsx: { 
      icon: Code, 
      color: 'text-cyan-600', 
      bgColor: 'bg-cyan-50',
      label: 'React Component' 
    },
    py: { 
      icon: Code, 
      color: 'text-blue-600', 
      bgColor: 'bg-blue-50',
      label: 'Python' 
    },
    sql: { 
      icon: Database, 
      color: 'text-gray-600', 
      bgColor: 'bg-gray-50',
      label: 'SQL Database' 
    },
    json: { 
      icon: Code, 
      color: 'text-green-600', 
      bgColor: 'bg-green-50',
      label: 'JSON' 
    },
    default: { 
      icon: File, 
      color: config.colors.neutral.text.secondary, 
      bgColor: config.colors.neutral['100'],
      label: 'File' 
    }
  };

  // Sample files data
  const files = [
    {
      id: 1,
      name: "Q4_Financial_Report.pdf",
      type: "pdf",
      size: 2456789,
      uploadDate: "2024-12-20 14:30",
      modifiedDate: "2024-12-20 14:30",
      uploadedBy: "Giulia Bianchi",
      shared: true,
      starred: true,
      permissions: "read",
      version: "v2.1",
      status: "approved",
      description: "Quarterly financial report for Q4 2024 including revenue, expenses, and projections.",
      tags: ["finance", "q4", "report", "2024"]
    },
    {
      id: 2,
      name: "Product_Roadmap_2025.pptx",
      type: "pptx",
      size: 5234567,
      uploadDate: "2024-12-19 09:15",
      modifiedDate: "2024-12-21 11:00",
      uploadedBy: "Marco Rossi",
      shared: true,
      starred: true,
      permissions: "edit",
      version: "v3.0",
      status: "in_review",
      description: "Product development roadmap and strategy presentation for 2025.",
      tags: ["product", "strategy", "2025", "presentation"]
    },
    {
      id: 3,
      name: "Team_Photos_Event.zip",
      type: "zip",
      size: 45678901,
      uploadDate: "2024-12-18 16:45",
      modifiedDate: "2024-12-18 16:45",
      uploadedBy: "Sofia Romano",
      shared: false,
      starred: false,
      permissions: "owner",
      version: "v1.0",
      status: "approved",
      description: "Company event photos from the annual holiday party.",
      tags: ["photos", "event", "team"]
    },
    {
      id: 4,
      name: "Customer_Database_Backup.sql",
      type: "sql",
      size: 8901234,
      uploadDate: "2024-12-21 03:00",
      modifiedDate: "2024-12-21 03:00",
      uploadedBy: "System Backup",
      shared: false,
      starred: false,
      permissions: "admin",
      version: "v1.0",
      status: "automated",
      description: "Automated daily backup of customer database.",
      tags: ["database", "backup", "automated"]
    },
    {
      id: 5,
      name: "Marketing_Campaign_Assets.zip",
      type: "zip",
      size: 67890123,
      uploadDate: "2024-12-15 10:30",
      modifiedDate: "2024-12-19 14:20",
      uploadedBy: "Marketing Team",
      shared: true,
      starred: false,
      permissions: "read",
      version: "v2.5",
      status: "approved",
      description: "Design assets, images, and videos for Q1 marketing campaign.",
      tags: ["marketing", "assets", "campaign", "design"]
    },
    {
      id: 6,
      name: "Employee_Handbook_2024.docx",
      type: "docx",
      size: 1234567,
      uploadDate: "2024-01-15 08:00",
      modifiedDate: "2024-11-30 15:30",
      uploadedBy: "HR Department",
      shared: true,
      starred: false,
      permissions: "read",
      version: "v4.2",
      status: "approved",
      description: "Company employee handbook with policies, procedures, and guidelines.",
      tags: ["hr", "handbook", "policies"]
    },
    {
      id: 7,
      name: "Sales_Analytics_Dashboard.xlsx",
      type: "xlsx",
      size: 3456789,
      uploadDate: "2024-12-20 11:00",
      modifiedDate: "2024-12-21 09:30",
      uploadedBy: "Alessandro Ferrari",
      shared: true,
      starred: true,
      permissions: "edit",
      version: "v1.8",
      status: "in_progress",
      description: "Interactive sales dashboard with real-time analytics and KPIs.",
      tags: ["sales", "analytics", "dashboard", "excel"]
    },
    {
      id: 8,
      name: "App_Component.jsx",
      type: "jsx",
      size: 45678,
      uploadDate: "2024-12-21 13:45",
      modifiedDate: "2024-12-21 16:00",
      uploadedBy: "Pietro Conti",
      shared: false,
      starred: false,
      permissions: "owner",
      version: "v1.3",
      status: "draft",
      description: "Main React component for the dashboard application.",
      tags: ["code", "react", "frontend"]
    }
  ];


  // Handler functions
  const handleFileUpload = () => {
    openDrawer(
      <SecureFileUpload/>
    );
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  // Get file type config
  const getFileTypeConfig = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    return fileTypes[extension] || fileTypes.default;
  };

  // Get status config
  const getStatusConfig = (status) => {
    switch(status) {
      case 'approved':
        return { icon: CheckCircle, color: config.colors.success.text, bg: config.colors.success['50'], label: 'Approved' };
      case 'in_review':
        return { icon: Clock, color: config.colors.warning.text, bg: config.colors.warning['50'], label: 'In Review' };
      case 'in_progress':
        return { icon: Clock, color: config.colors.primary.text, bg: config.colors.primary['50'], label: 'In Progress' };
      case 'draft':
        return { icon: FileText, color: config.colors.neutral.text.muted, bg: config.colors.neutral['100'], label: 'Draft' };
      case 'automated':
        return { icon: HardDrive, color: config.colors.neutral.text.secondary, bg: config.colors.neutral['100'], label: 'Automated' };
      default:
        return { icon: File, color: config.colors.neutral.text.muted, bg: config.colors.neutral['100'], label: status };
    }
  };

  // Filter files based on view mode
  const filteredFiles = files.filter(file => {
    switch(viewMode) {
      case 'recent':
        const date = new Date(file.uploadDate);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return date > weekAgo;
      case 'starred':
        return file.starred;
      case 'shared':
        return file.shared;
      default:
        return true;
    }
  });

  const handleFileClick = (file) => {
    setSelectedFile(selectedFile?.id === file.id ? null : file);
  };

  return (
    <div className={`${config.container.minHeight} ${config.container.backgroundColor} ${config.container.paddingLarge}`}>
      <div className={`${config.container.maxWidth} ${config.container.marginX}`}>
        {/* Header */}
        <div className={`${config.layout.flexBetween} mb-6`}>
          <h1 className={`${config.typography.pageTitle} ${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.medium}`}>
            <FolderOpen className={`${config.icons.large} ${config.colors.primary.text}`} />
            Files Manager
          </h1>
          <div className={`${config.layout.flex} ${config.layout.flexGap.medium}`}>
            <button className={config.buttons.secondary}>
              <Search className={`${config.icons.medium} mr-2`} />
              Search
            </button>
            <button 
              onClick={() => handleFileUpload()}
              className={config.buttons.primary}
              title="Upload Files"
            >
              <Upload className={`${config.icons.medium} mr-2`} />
              Upload Files
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className={`${config.layout.flex} ${config.layout.flexGap.medium} mb-6 border-b ${config.colors.neutral.border.default}`}>
          <button
            onClick={() => setViewMode('all')}
            className={`pb-3 px-1 border-b-2 ${config.animations.transition.colors} ${
              viewMode === 'all' 
                ? `${config.colors.primary.border} ${config.colors.primary.text}` 
                : 'border-transparent ' + config.colors.neutral.text.muted
            }`}
          >
            <span className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
              <File className={config.icons.medium} />
              All Files
              <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${config.colors.neutral['100']} ${config.colors.neutral.text.secondary}`}>
                {files.length}
              </span>
            </span>
          </button>
          <button
            onClick={() => setViewMode('recent')}
            className={`pb-3 px-1 border-b-2 ${config.animations.transition.colors} ${
              viewMode === 'recent' 
                ? `${config.colors.primary.border} ${config.colors.primary.text}` 
                : 'border-transparent ' + config.colors.neutral.text.muted
            }`}
          >
            <span className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
              <Clock className={config.icons.medium} />
              Recent
            </span>
          </button>
          <button
            onClick={() => setViewMode('starred')}
            className={`pb-3 px-1 border-b-2 ${config.animations.transition.colors} ${
              viewMode === 'starred' 
                ? `${config.colors.primary.border} ${config.colors.primary.text}` 
                : 'border-transparent ' + config.colors.neutral.text.muted
            }`}
          >
            <span className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
              <Star className={config.icons.medium} />
              Starred
            </span>
          </button>
          <button
            onClick={() => setViewMode('shared')}
            className={`pb-3 px-1 border-b-2 ${config.animations.transition.colors} ${
              viewMode === 'shared' 
                ? `${config.colors.primary.border} ${config.colors.primary.text}` 
                : 'border-transparent ' + config.colors.neutral.text.muted
            }`}
          >
            <span className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
              <Share2 className={config.icons.medium} />
              Shared
            </span>
          </button>
        </div>

        {/* Files List */}
        <div className={config.card.spacing}>
          {filteredFiles.map((file) => {
            const fileType = getFileTypeConfig(file.name);
            const statusConfig = getStatusConfig(file.status);
            const isExpanded = selectedFile?.id === file.id;

            return (
              <div 
                key={file.id}
                className={`${config.card.hover} ${config.animations.transition.all} ${
                  isExpanded ? `${config.shadows.lg} scale-[1.01]` : ''
                }`}
                onClick={() => handleFileClick(file)}
              >
                <div className={config.card.padding}>
                  {/* Main File Row */}
                  <div className={`${config.layout.flex} ${config.layout.flexGap.large}`}>
                    {/* File Type Icon */}
                    <div className={`p-3 ${config.borderRadius.lg} ${config.colors.neutral['50']} flex-shrink-0`}>
                      <fileType.icon className={`${config.icons.large} ${config.colors.neutral.text.secondary}`} />
                    </div>

                    {/* File Details - Left Aligned */}
                    <div className="flex-1 min-w-0">
                      <div className={`${config.layout.flex} ${config.layout.flexGap.medium} mb-1`}>
                        <h3 className={`${config.typography.heading} text-base font-semibold truncate`}>
                          {file.name}
                        </h3>
                        {file.starred && (
                          <Star className={`${config.icons.small} ${config.colors.warning.text} fill-current`} />
                        )}
                        {file.shared && (
                          <Share2 className={`${config.icons.small} ${config.colors.primary.text}`} />
                        )}
                        <span className={`px-2 py-0.5 text-xs ${config.borderRadius.md} ${statusConfig.bg} ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                        <span className={`text-xs ${config.colors.neutral.text.muted}`}>
                          {file.version}
                        </span>
                      </div>
                      
                      <p className={`${config.typography.body} mb-2 truncate`}>
                        {file.description}
                      </p>

                      {/* File Info Row */}
                      <div className={`${config.layout.flex} flex-wrap ${config.layout.flexGap.large} ${config.typography.small}`}>
                        <div className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
                          <HardDrive className={config.icons.small} />
                          <span>{formatFileSize(file.size)}</span>
                        </div>
                        <div className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
                          <Clock className={config.icons.small} />
                          <span>Modified {formatDate(file.modifiedDate)}</span>
                        </div>
                        <div className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
                          <User className={config.icons.small} />
                          <span>{file.uploadedBy}</span>
                        </div>
                        <div className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small}`}>
                          {file.permissions === 'owner' || file.permissions === 'admin' ? (
                            <Unlock className={config.icons.small} />
                          ) : (
                            <Lock className={config.icons.small} />
                          )}
                          <span className="capitalize">{file.permissions}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right Side Actions */}
                    <div className="flex flex-col items-end justify-between flex-shrink-0">
                      <div className={`${config.layout.flex} ${config.layout.flexGap.small}`}>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('Download:', file.name);
                          }}
                          className={`${config.buttons.ghost} ${config.colors.primary.text}`}
                          title="Download"
                        >
                          <Download className={config.icons.normal} />
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('Options for:', file.name);
                          }}
                          className={config.buttons.ghost}
                        >
                          <MoreVertical className={config.icons.normal} />
                        </button>
                      </div>
                      
                      <div className={`${config.layout.flexCol} items-end ${config.layout.flexGap.small}`}>
                        <span className={`text-sm font-medium ${config.colors.neutral.text.secondary}`}>
                          {fileType.label}
                        </span>
                        <span className={config.typography.small}>
                          Uploaded {formatDate(file.uploadDate)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className={`mt-4 pt-4 border-t ${config.colors.neutral.border.light}`}>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* File Details */}
                        <div>
                          <h4 className={`${config.typography.label} mb-3`}>File Details</h4>
                          <div className={config.card.spacing}>
                            <div className={`${config.layout.flex} ${config.layout.flexBetween}`}>
                              <span className={config.typography.small}>Full Name:</span>
                              <span className={`${config.typography.small} font-medium`}>{file.name}</span>
                            </div>
                            <div className={`${config.layout.flex} ${config.layout.flexBetween}`}>
                              <span className={config.typography.small}>Size:</span>
                              <span className={`${config.typography.small} font-medium`}>{formatFileSize(file.size)}</span>
                            </div>
                            <div className={`${config.layout.flex} ${config.layout.flexBetween}`}>
                              <span className={config.typography.small}>Type:</span>
                              <span className={`${config.typography.small} font-medium`}>{fileType.label}</span>
                            </div>
                            <div className={`${config.layout.flex} ${config.layout.flexBetween}`}>
                              <span className={config.typography.small}>Version:</span>
                              <span className={`${config.typography.small} font-medium`}>{file.version}</span>
                            </div>
                          </div>
                        </div>

                        {/* Tags */}
                        <div>
                          <h4 className={`${config.typography.label} mb-3`}>Tags</h4>
                          <div className={`${config.layout.flex} flex-wrap ${config.layout.flexGap.small}`}>
                            {file.tags.map((tag, index) => (
                              <span 
                                key={index} 
                                className={`px-2 py-1 text-xs ${config.borderRadius.md} ${config.colors.neutral['100']} ${config.colors.neutral.text.secondary}`}
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Actions */}
                        <div>
                          <h4 className={`${config.typography.label} mb-3`}>Quick Actions</h4>
                          <div className={`${config.layout.flexCol} ${config.layout.flexGap.small}`}>
                            <button className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.medium} w-full p-2 text-left ${config.borderRadius.md} ${config.animations.transition.colors} hover:${config.colors.neutral['100']}`}>
                              <Eye className={config.icons.small} />
                              <span className={config.typography.small}>Preview</span>
                            </button>
                            <button className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.medium} w-full p-2 text-left ${config.borderRadius.md} ${config.animations.transition.colors} hover:${config.colors.neutral['100']}`}>
                              <Share2 className={config.icons.small} />
                              <span className={config.typography.small}>Share</span>
                            </button>
                            {(file.permissions === 'owner' || file.permissions === 'admin') && (
                              <button className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.medium} w-full p-2 text-left ${config.borderRadius.md} ${config.animations.transition.colors} hover:${config.colors.danger['50']} ${config.colors.danger.text}`}>
                                <Trash2 className={config.icons.small} />
                                <span className={config.typography.small}>Delete</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Storage Info Footer */}
        <div className={`mt-6 p-4 ${config.borderRadius.lg} ${config.colors.neutral['100']}`}>
          <div className={`${config.layout.flexBetween}`}>
            <div className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.medium}`}>
              <HardDrive className={`${config.icons.normal} ${config.colors.neutral.text.secondary}`} />
              <span className={config.typography.body}>
                Storage Used: <span className="font-semibold">
                  {formatFileSize(files.reduce((acc, file) => acc + file.size, 0))}
                </span> of 10 GB
              </span>
            </div>
            <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full ${config.colors.primary['500']} transition-all duration-500`}
                style={{ width: `${(files.reduce((acc, file) => acc + file.size, 0) / (10 * 1024 * 1024 * 1024)) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilesList;